/**
 * Midpoint API Service
 * Handles all API operations with the midpoint server
 */

import { getMidpointAuthHeaders, MidpointAuthService, createMidpointAuthFromEnv, MidpointAuthenticationError, MidpointAuthError } from './midpointAuth';

/**
 * Midpoint's JSON serialization returns a bare object (not a single-element
 * array) for any multi-valued container field that currently holds exactly
 * one value -- e.g. roleMembershipRef, assignment. Reads of those fields must
 * go through this first, or they work until a real record has exactly one
 * value, then crash (mirrors as_list() in bff/app/domain.py).
 */
function asArray<T>(value: T | T[] | null | undefined): T[] {
  if (value === null || value === undefined) return [];
  return Array.isArray(value) ? value : [value];
}

// ============== INTERFACES ==============

// Current application interfaces (maintained for compatibility)
export interface Group {
  id: string;
  displayName: string;
  description: string;
  createdDateTime: string;
}

export interface Member {
  displayName: string;
  userPrincipalName: string;
  jobTitle: string;
  id: string;
}

// Midpoint API response interfaces
interface MidpointUser {
  oid: string;
  name: string;
  fullName?: {
    orig: string;
  };
  emailAddress?: string;
  title?: string;
  organizationalUnit?: string;
  assignment?: MidpointAssignment[];
  roleMembershipRef?: MidpointRef[];
}

interface MidpointResource {
  oid: string;
  name: string;
  displayName?: string;
  description?: string;
}

interface MidpointShadow {
  oid: string;
  name: string;
  attributes?: {
    [key: string]: any;
  };
  resourceRef?: MidpointRef;
}

interface MidpointRole {
  oid: string;
  name: string;
  description?: string;
  displayName?: string;
  metadata?: {
    createTimestamp: string;
  };
}

interface MidpointRef {
  oid: string;
  type: string;
  targetName?: string | { orig?: string; norm?: string };
}

interface MidpointAssignment {
  targetRef?: MidpointRef;
  activation?: {
    effectiveStatus: string;
  };
}

interface MidpointWorkItem {
  id: number;
  assigneeRef?: MidpointRef[];
  createTimestamp?: string;
  deadline?: string;
  output?: { outcome?: string; comment?: string };
}

export interface MidpointCase {
  oid: string;
  name?: string;
  state?: string;
  requestorRef?: MidpointRef;
  objectRef?: MidpointRef;
  targetRef?: MidpointRef;
  workItem?: MidpointWorkItem[];
  metadata?: { createTimestamp?: string };
  startTimestamp?: string;
  openTimestamp?: string;
}

interface MidpointSearchResult<T> {
  object: {
    object: T[];
  };
}

interface MidpointSingleResult<T> {
  object: T;
}

// ============== API SERVICE ==============

export class MidpointApiService {
  private baseUrl: string;
  private authService: MidpointAuthService;

  constructor(baseUrl?: string, authService?: MidpointAuthService) {
    this.baseUrl = baseUrl || process.env.REACT_APP_MIDPOINT_BASE_URL || '';
    this.authService = authService || createMidpointAuthFromEnv();
  }

  /** Safely convert a Midpoint PolyString or plain string to a string */
  private polyString(value: any): string {
    if (!value) return '';
    if (typeof value === 'object') return value.orig || value.norm || '';
    return String(value);
  }

  /**
   * Make authenticated API call to midpoint server
   */
  private async makeRequest<T>(
    endpoint: string,
    options: {
      method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
      body?: any;
    } = {}
  ): Promise<T> {
    const { method = 'GET', body } = options;

    const url = `${this.baseUrl}${endpoint}`;
    const headers = await getMidpointAuthHeaders();

    const requestOptions: RequestInit = {
      method,
      headers,
    };

    if (body && method !== 'GET') {
      requestOptions.body = JSON.stringify(body);
    }

    try {
      const response = await fetch(url, requestOptions);

      if (!response.ok) {
        await this.handleApiError(response);
      }

      // Handle empty responses (like 204 No Content)
      const contentType = response.headers.get('content-type') || '';
      if (response.status === 204 || !contentType.includes('application/json')) {
        // An HTML body on a 2xx is never an API response -- it means the request
        // never reached the API. That is exactly what a wrong base URL produces:
        // with REACT_APP_MIDPOINT_BASE_URL unset, this service falls back to ''
        // and calls "/self" instead of "/api/self", which misses CloudFront's
        // /api/* behaviour, lands on the S3 origin, and comes back as index.html
        // with HTTP 200. Returning {} here made that indistinguishable from an
        // empty response, so the app reported "No Qtrust user found" while the
        // BFF logged nothing at all -- the request never got there. Name the
        // cause instead. (No prefix: the catch below adds "Qtrust API Error".)
        if (contentType.includes('text/html')) {
          throw new Error(
            `${url} returned HTML (HTTP ${response.status}), not JSON -- the API ` +
            `base URL is wrong. REACT_APP_MIDPOINT_BASE_URL is "${this.baseUrl}", ` +
            `it should be "/api".`
          );
        }
        console.log(`Received ${response.status} response with no content`);
        return {} as T;
      }

      const data = await response.json();
      return data;
    } catch (error: any) {
      if (error instanceof MidpointAuthenticationError) {
        throw error;
      }

      throw new Error(`Qtrust API Error: ${error.message}`);
    }
  }

  /**
   * Make an API call and also return the raw response headers
   * Used for paginated searches where total count comes from @TotalCount header
   */
  private async makeRequestWithHeaders<T>(
    endpoint: string,
    options: {
      method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
      body?: any;
    } = {}
  ): Promise<{ data: T; headers: Headers }> {
    const { method = 'GET', body } = options;

    const url = `${this.baseUrl}${endpoint}`;
    const headers = await getMidpointAuthHeaders();

    const requestOptions: RequestInit = {
      method,
      headers,
    };

    if (body && method !== 'GET') {
      requestOptions.body = JSON.stringify(body);
    }

    try {
      const response = await fetch(url, requestOptions);

      if (!response.ok) {
        await this.handleApiError(response);
      }

      if (response.status === 204 || !response.headers.get('content-type')?.includes('application/json')) {
        return { data: {} as T, headers: response.headers };
      }

      const data = await response.json();
      return { data, headers: response.headers };
    } catch (error: any) {
      if (error instanceof MidpointAuthenticationError) {
        throw error;
      }
      throw new Error(`Qtrust API Error: ${error.message}`);
    }
  }

  /**
   * Handle API error responses
   */
  private async handleApiError(response: Response): Promise<never> {
    const statusCode = response.status;
    let errorMessage = `HTTP ${statusCode}: ${response.statusText}`;

    try {
      const errorBody = await response.text();
      if (errorBody) {
        const errorData = JSON.parse(errorBody);
        // Midpoint wraps errors in object.message or object.details
        errorMessage = errorData?.object?.message
          || errorData?.object?.details?.split('\n')[0]
          || errorData.message
          || errorMessage;
        console.error('[Midpoint Error Response]', JSON.stringify(errorData, null, 2));
      }
    } catch {
      // Ignore JSON parse errors, use default message
    }

    switch (statusCode) {
      case 401:
        throw new MidpointAuthenticationError(
          MidpointAuthError.AUTHENTICATION_FAILED,
          'Invalid credentials or authentication failed',
          statusCode
        );
      case 403:
        throw new MidpointAuthenticationError(
          MidpointAuthError.UNAUTHORIZED_ACCESS,
          'Access denied. Insufficient permissions.',
          statusCode
        );
      case 404:
        throw new Error(`Resource not found: ${errorMessage}`);
      case 500:
        throw new Error(`Qtrust server error: ${errorMessage}`);
      default:
        throw new Error(`API Error: ${errorMessage}`);
    }
  }

  // ============== USER OPERATIONS ==============

  /**
   * Find user by email address
   */
  async findUserByEmail(email: string): Promise<MidpointUser | null> {
    const searchQuery = {
      query: {
        filter: {
          equal: {
            path: "emailAddress",
            value: email
          }
        }
      }
    };

    try {
      const result = await this.makeRequest<any>(
        '/users/search',
        {
          method: 'POST',
          body: searchQuery
        }
      );

      const users = result.object?.user || result.object?.object || [];
      return users.length > 0 ? users[0] : null;
    } catch (error) {
      console.error('Error finding user by email:', error);
      throw error;
    }
  }

  /**
   * Get user details by OID including role memberships
   */
  async getUserWithRoles(userOid: string): Promise<MidpointUser> {
    try {
      console.log(`Fetching user details for OID: ${userOid}`);

      const result = await this.makeRequest<any>(
        `/users/${userOid}`
      );

      const user = result?.user ?? result?.object ?? result;
      if (!user?.oid) {
        throw new Error(`User with OID ${userOid} not found in response`);
      }

      console.log(`Successfully retrieved user: ${user.name || 'unnamed'}`);
      return user as MidpointUser;
    } catch (error) {
      console.error(`Error getting user with roles for OID ${userOid}:`, error);

      if (error instanceof Error) {
        if (error.message.includes('404') || error.message.includes('not found')) {
          throw new Error(`User with OID ${userOid} does not exist`);
        }
        if (error.message.includes('403') || error.message.includes('Access denied')) {
          throw new Error(`Access denied when fetching user ${userOid}. Check permissions.`);
        }
      }

      throw error;
    }
  }

  // ============== ROLE OPERATIONS ==============

  /**
   * Get role details by OID
   */
  async getRole(roleOid: string): Promise<MidpointRole> {
    try {
      console.log(`Getting role details for OID: ${roleOid}`);
      console.log(`Making request to: /roles/${roleOid}`);

      const result = await this.makeRequest<any>(`/roles/${roleOid}`);

      const role = result?.role ?? result?.object ?? result;
      if (!role?.oid) {
        throw new Error(`Role with OID ${roleOid} not found in response`);
      }

      return role as MidpointRole;
    } catch (error) {
      console.error(`=== GET ROLE ERROR ===`);
      console.error(`Role OID: ${roleOid}`);
      console.error('Error details:', error);

      if (error instanceof Error) {
        if (error.message.includes('404') || error.message.includes('not found')) {
          throw new Error(`Role with OID ${roleOid} does not exist or you don't have permission to access it`);
        }
        if (error.message.includes('403') || error.message.includes('Access denied')) {
          throw new Error(`Access denied when accessing role ${roleOid}. Check permissions.`);
        }
      }

      throw error;
    }
  }

  /**
   * Search for users who have a specific role assigned
   */
  async getRoleMembers(roleOid: string): Promise<MidpointUser[]> {
    const searchQuery = {
      query: {
        filter: {
          ref: {
            path: "assignment/targetRef",
            value: {
              oid: roleOid,
              type: "RoleType"
            }
          }
        }
      }
    };

    try {
      const result = await this.makeRequest<any>(
        '/users/search',
        {
          method: 'POST',
          body: searchQuery
        }
      );

      return result.object?.user || result.object?.object || [];
    } catch (error) {
      console.error('Error getting role members:', error);
      throw error;
    }
  }

  // ============== HIGH-LEVEL OPERATIONS ==============

  /**
   * Get all available groups (roles) in the system
   * This is simpler and more appropriate for showing all groups on the dashboard
   */
  async getUserAssignedRoles(email: string): Promise<Group[]> {
    const user = await this.findUserByEmail(email);
    if (!user) return [];

    const [fullUser, allRoles] = await Promise.all([
      this.getUserWithRoles(user.oid),
      this.getAllGroups(),
    ]);

    const roleOidSet = new Set(
      asArray(fullUser.roleMembershipRef)
        .filter((ref: any) => ref.type === 'RoleType' || ref.type === 'c:RoleType')
        .map((ref: any) => ref.oid)
    );

    return allRoles.filter((g) => roleOidSet.has(g.id));
  }

  async getRequestableRoles(): Promise<Group[]> {
    return this.getAllGroups();
  }

  async getAllGroups(): Promise<Group[]> {
    try {
      const searchQuery = { query: {} };

      console.log('Making roles/search request with query:', searchQuery);
      const result = await this.makeRequest<any>(
        '/roles/search',
        {
          method: 'POST',
          body: searchQuery
        }
      );

      const roles = result.object?.object || result.object?.role || [];
      console.log(`Found ${roles.length} roles in midpoint system:`, roles);
      return roles.map((role: MidpointRole) => this.transformRoleToGroup(role));
    } catch (error) {
      console.error('Error getting all groups:', error);

      // More detailed error logging
      if (error instanceof Error) {
        console.error('Error details:', {
          name: error.name,
          message: error.message,
          stack: error.stack
        });
      }

      return [];
    }
  }

  /**
   * Get groups (roles) with server-side pagination.
   * Uses Midpoint query.paging for offset/maxSize; total count from @TotalCount header.
   */
  async getGroupsPaginated(page: number, pageSize: number): Promise<{ groups: Group[]; total: number }> {
    const offset = (page - 1) * pageSize;
    const searchQuery = {
      query: {
        paging: {
          offset,
          maxSize: pageSize,
        }
      }
    };

    const { data } = await this.makeRequestWithHeaders<any>(
      '/roles/search',
      { method: 'POST', body: searchQuery }
    );

    console.log('getGroupsPaginated raw response:', JSON.stringify(data, null, 2));

    // Midpoint can return the list at different paths depending on version
    const roles: MidpointRole[] =
      data?.object?.object ||
      data?.object?.role ||
      data?.searchResultList?.searchResult ||
      data?.object ||
      (Array.isArray(data) ? data : []);

    // Midpoint returns total count as @TotalCount in the object wrapper
    const rawTotal =
      data?.object?.['@TotalCount'] ??
      data?.['@TotalCount'] ??
      data?.object?.totalCount ??
      null;

    console.log('Total count raw value:', rawTotal, '| keys in data.object:', data?.object ? Object.keys(data.object) : 'N/A');

    // If we can't get the real total, estimate: if we got a full page there's at least one more
    const total: number = rawTotal !== null
      ? Number(rawTotal)
      : roles.length === pageSize ? page * pageSize + 1 : (page - 1) * pageSize + roles.length;

    console.log(`getGroupsPaginated: ${roles.length} roles on page ${page}, total=${total}`);

    return {
      groups: roles.map(role => this.transformRoleToGroup(role)),
      total,
    };
  }

  /**
   * Get groups (roles) for a user by email address - LEGACY METHOD
   * Kept for backward compatibility, but getAllGroups() is simpler for dashboard
   */
  async getUserGroups(email: string): Promise<Group[]> {
    try {
      // Step 1: Find user by email
      const user = await this.findUserByEmail(email);
      if (!user) {
        return [];
      }

      // Step 2: Get user with role memberships
      const userWithRoles = await this.getUserWithRoles(user.oid);

      // Step 3: Get role details for each roleRef
      const roleRefs = asArray(userWithRoles.roleMembershipRef);
      const groups: Group[] = [];

      for (const roleRef of roleRefs) {
        if (roleRef.type === 'RoleType' || roleRef.type === 'c:RoleType') {
          try {
            const role = await this.getRole(roleRef.oid);
            groups.push(this.transformRoleToGroup(role));
          } catch (error) {
            console.warn(`Failed to get role details for ${roleRef.oid}:`, error);
            // Continue with other roles
          }
        }
      }

      return groups;
    } catch (error) {
      console.error('Error getting user groups:', error);
      throw error;
    }
  }

  /**
   * Get members of a group (role) by role OID
   */
  async getGroupMembers(roleOid: string): Promise<Member[]> {
    try {
      const users = await this.getRoleMembers(roleOid);
      return users.map(user => this.transformUserToMember(user));
    } catch (error) {
      console.error('Error getting group members:', error);
      throw error;
    }
  }

  // ============== DATA TRANSFORMATION ==============

  /**
   * Transform midpoint role to application group format
   */
  private transformRoleToGroup(role: MidpointRole): Group {
    return {
      id: role.oid,
      displayName: this.polyString(role.displayName || role.name),
      description: this.polyString(role.description || ''),
      createdDateTime: role.metadata?.createTimestamp || ""
    };
  }

  /**
   * Transform midpoint user to application member format
   */
  private transformUserToMember(user: MidpointUser): Member {
    return {
      id: user.oid,
      displayName: this.polyString(user.fullName) || user.name,
      userPrincipalName: user.emailAddress || user.name,
      jobTitle: this.polyString(user.title) || this.polyString(user.organizationalUnit) || ''
    };
  }

  /**
   * Transform shadow object to application member format
   */
  private transformShadowToMember(shadow: MidpointShadow): Member {
    const attrs = shadow.attributes || {};

    return {
      id: `shadow-${shadow.oid}`, // Prefix to distinguish from repository users
      displayName: attrs.displayName || attrs.cn || attrs.name || shadow.name,
      userPrincipalName: attrs.mail || attrs.userPrincipalName || attrs.email || shadow.name,
      jobTitle: attrs.title || attrs.department || attrs.jobTitle || ''
    };
  }

  // ============== UTILITY METHODS ==============

  /**
   * Test connection to midpoint server
   */
  async testConnection(): Promise<boolean> {
    try {
      // Try to get a simple object to test connection and auth
      await this.makeRequest('/self');
      return true;
    } catch (error) {
      console.error('Midpoint connection test failed:', error);
      return false;
    }
  }

  /**
   * Get all available resources (connected systems like Azure AD)
   */
  async getResources(): Promise<MidpointResource[]> {
    try {
      const searchQuery = {
        query: {}  // Empty query to get all resources
      };

      console.log('Making resources/search request to get all connected resources');
      const result = await this.makeRequest<any>(
        '/resources/search',
        {
          method: 'POST',
          body: searchQuery
        }
      );

      const resources = result.object?.resource || result.object?.object || [];
      console.log(`Found ${resources.length} connected resources`);
      return resources;
    } catch (error) {
      console.error('Error getting resources:', error);
      throw error;
    }
  }

  /**
   * Search shadow objects (external accounts) from a specific resource
   */
  async getResourceUsers(resourceOid: string): Promise<Member[]> {
    try {
      console.log(`Searching shadow objects for resource ${resourceOid}`);
      const result = await this.makeRequest<any>(
        `/shadows/search`,
        {
          method: 'POST',
          body: {
            query: {
              filter: {
                and: {
                  ref: {
                    path: "resourceRef",
                    value: {
                      oid: resourceOid,
                      type: "ResourceType"
                    }
                  },
                  equal: {
                    path: "kind",
                    value: "account"
                  }
                }
              }
            }
          }
        }
      );

      const shadows = result.object?.shadow || result.object?.object || [];
      console.log(`Found ${shadows.length} shadow objects for resource`);
      return shadows.map((shadow: any) => this.transformShadowToMember(shadow));
    } catch (error) {
      console.error('Error getting resource users:', error);
      throw error;
    }
  }

  /**
   * Get all users in the system (both Midpoint repository and external resources)
   */
  async getAllUsers(): Promise<Member[]> {
    try {
      console.log('Getting all users from Midpoint repository and connected resources...');

      // Get users from Midpoint repository
      const repoUsers = await this.getRepositoryUsers();

      // Get users from external resources (like Azure AD)
      const resourceUsers = await this.getAllResourceUsers();

      // Combine and deduplicate users based on email
      const allUsers = [...repoUsers, ...resourceUsers];
      const uniqueUsers = this.deduplicateUsersByEmail(allUsers);

      console.log(`Total unique users found: ${uniqueUsers.length} (${repoUsers.length} from repository, ${resourceUsers.length} from external resources)`);
      return uniqueUsers;
    } catch (error) {
      console.error('Error getting all users:', error);
      throw error;
    }
  }

  /**
   * Get users from Midpoint repository only
   */
  private async getRepositoryUsers(): Promise<Member[]> {
    try {
      const searchQuery = {
        query: {}  // Empty query to get all users
      };

      console.log('Making users/search request to get repository users');
      const result = await this.makeRequest<any>(
        '/users/search',
        {
          method: 'POST',
          body: searchQuery
        }
      );

      const users = result.object?.user || result.object?.object || [];
      console.log(`Found ${users.length} users in midpoint repository`);
      return users.map((user: any) => this.transformUserToMember(user));
    } catch (error) {
      console.error('Error getting repository users:', error);
      throw error;
    }
  }

  /**
   * Get users from all connected external resources
   */
  private async getAllResourceUsers(): Promise<Member[]> {
    // Let getResources() errors propagate — don't silently swallow them
    const resources = await this.getResources();

    const resourceUserPromises = resources.map(resource =>
      this.getResourceUsers(resource.oid)
    );

    const resourceUserArrays = await Promise.allSettled(resourceUserPromises);

    // Log individual resource failures without hiding them
    resourceUserArrays.forEach((result, i) => {
      if (result.status === 'rejected') {
        console.error(`Failed to fetch users from resource ${resources[i]?.name || resources[i]?.oid}:`, result.reason);
      }
    });

    const resourceUsers = resourceUserArrays
      .filter((result): result is PromiseFulfilledResult<Member[]> => result.status === 'fulfilled')
      .flatMap(result => result.value);

    console.log(`Found ${resourceUsers.length} users from external resources`);
    return resourceUsers;
  }

  /**
   * Remove duplicate users based on email address
   */
  private deduplicateUsersByEmail(users: Member[]): Member[] {
    const emailMap = new Map<string, Member>();

    users.forEach(user => {
      const email = user.userPrincipalName.toLowerCase();

      // If we already have a user with this email, prefer repository users over shadow users
      if (emailMap.has(email)) {
        const existing = emailMap.get(email)!;
        // Prefer users with more complete information (e.g., those with IDs from repository)
        if (!existing.id.startsWith('shadow-') && user.id.startsWith('shadow-')) {
          return; // Keep existing repository user
        }
      }

      emailMap.set(email, user);
    });

    return Array.from(emailMap.values());
  }

  /**
   * Get all users in the system
   * @deprecated Use getAllUsers() instead for better coverage
   */
  async getAllUsers_Legacy(): Promise<Member[]> {
    try {
      // Search for all users in the system
      const searchQuery = {
        query: {}  // Empty query to get all users
      };

      console.log('Making users/search request to get all users');
      const result = await this.makeRequest<any>(
        '/users/search',
        {
          method: 'POST',
          body: searchQuery
        }
      );

      const users = result.object?.user || result.object?.object || [];
      console.log(`Found ${users.length} users in midpoint system`);
      return users.map((user: any) => this.transformUserToMember(user));
    } catch (error) {
      console.error('Error getting all users:', error);
      throw error;
    }
  }

  /**
   * Add a member (user) to a group (role) by creating an assignment
   * Handles both repository users and shadow users (external accounts)
   */
  async addMemberToGroup(userOid: string, roleOid: string): Promise<void> {
    // TEMPORARY, for local BFF testing: the BFF's POST /roles/{roleId}/members
    // already handles shadow-prefix detection and owner-resolution internally
    // -- see bff/app/routes/roles.py.
    try {
      await this.makeRequest(`/roles/${roleOid}/members`, {
        method: 'POST',
        body: { userId: userOid },
      });
    } catch (error) {
      console.error('Error adding member to group via BFF:', error);
      throw error;
    }
  }

  /**
   * Verify that both user and role exist before attempting assignment
   */
  private async verifyUserAndRoleExist(userOid: string, roleOid: string): Promise<void> {
    try {
      console.log('Verifying user and role existence...');

      // Check if user exists
      try {
        const user = await this.makeRequest(`/users/${userOid}`);
        console.log('✅ User exists:', user);
      } catch (userError) {
        console.error('❌ User verification failed:', userError);
        throw new Error(`User with OID ${userOid} does not exist or is not accessible`);
      }

      // Check if role exists
      try {
        const role = await this.makeRequest(`/roles/${roleOid}`);
        console.log('✅ Role exists:', role);
      } catch (roleError) {
        console.error('❌ Role verification failed:', roleError);
        throw new Error(`Role with OID ${roleOid} does not exist or is not accessible`);
      }

      console.log('✅ Both user and role verified successfully');
    } catch (error) {
      console.error('Entity verification failed:', error);
      throw error;
    }
  }



  /**
   * Find the MidPoint repository user who owns a shadow, then assign the role to that user.
   * Shadows (external accounts) cannot have roles assigned directly — only users can.
   */
  private async importShadowUser(shadowOid: string, roleOid: string): Promise<void> {
    try {
      // Fetch the shadow to get its ownerRef (linked MidPoint user)
      const shadowResult = await this.makeRequest<MidpointSingleResult<any>>(
        `/shadows/${shadowOid}`
      );

      console.log(`Shadow response:`, JSON.stringify(shadowResult?.object, null, 2));

      const ownerRef = shadowResult?.object?.ownerRef;
      let userOid: string;

      if (ownerRef?.oid) {
        userOid = ownerRef.oid;
        console.log(`Shadow ${shadowOid} is owned by user ${userOid} via ownerRef.`);
      } else {
        // Shadow not correlated — search for user by shadow name (Azure AD UPN)
        const shadowName = shadowResult?.object?.name;
        console.log(`Shadow has no ownerRef, searching for user by name/email: ${shadowName}`);

        if (!shadowName) {
          throw new Error(`Shadow ${shadowOid} has no ownerRef and no name attribute. Cannot find linked user.`);
        }

        const user = await this.findUserByEmail(shadowName);
        if (!user) {
          throw new Error(`Shadow ${shadowOid} is not correlated and no Qtrust user found with email/name: ${shadowName}`);
        }

        userOid = user.oid;
        console.log(`Found MidPoint user ${userOid} matching shadow name ${shadowName}`);
      }
      console.log(`Shadow ${shadowOid} is owned by user ${userOid}, assigning role there.`);

      const objectModification = {
        objectModification: {
          itemDelta: {
            modificationType: "add",
            path: "assignment",
            value: [
              {
                targetRef: {
                  oid: roleOid,
                  type: "RoleType"
                },
                activation: {
                  effectiveStatus: "enabled"
                }
              }
            ]
          }
        }
      };

      await this.makeRequest(
        `/users/${userOid}`,
        {
          method: 'PATCH',
          body: objectModification
        }
      );

      console.log(`Successfully assigned role ${roleOid} to user ${userOid} via shadow ${shadowOid}`);
    } catch (error) {
      console.error('Error assigning role via shadow user:', error);
      throw error;
    }
  }

  /**
   * Remove a member (user) from a group (role)
   */
  async removeMemberFromGroup(userOid: string, roleOid: string): Promise<void> {
    const actualUserOid = userOid.replace('shadow-', '');
    try {
      await this.makeRequest(`/roles/${roleOid}/members/${actualUserOid}`, { method: 'DELETE' });
    } catch (error) {
      console.error('Error removing member from group via BFF:', error);
      throw error;
    }
  }

  /**
   * Get authentication status
   */
  isAuthenticated(): boolean {
    return this.authService.isConfigured();
  }

  // ============== SELF / CURRENT USER ==============

  async getSelf(): Promise<MidpointUser> {
    // Why /self can fail and still not be fatal: the email lookup below is a
    // legitimate fallback, since the signed-in principal need not be the
    // MidPoint focus. Swallowing the REASON, however, is not legitimate -- a
    // failing /self surfaced as "No Qtrust user found for email: ...", which
    // sends you to MidPoint's user list when the real fault was routing or
    // auth. Keep the fallback; record why it was taken and carry it forward.
    let selfError: string | null = null;
    try {
      const result = await this.makeRequest<any>('/self');
      // /self returns { user: { oid, ... } } (type-keyed envelope)
      const user = result?.user ?? result?.object ?? result;
      if (user?.oid) return user as MidpointUser;
      selfError = '/self returned no user oid';
      console.warn('[Qtrust] /self returned no user oid, falling back to email lookup');
    } catch (error) {
      selfError = error instanceof Error ? error.message : String(error);
      console.warn('[Qtrust] /self failed, falling back to email lookup:', selfError);
    }
    // Fallback: find by email from MSAL account
    const accounts = (await import('../auth/msalInstance')).default.getAllAccounts();
    const email = accounts[0]?.username;
    if (!email) throw new Error('Could not identify current user');
    const user = await this.findUserByEmail(email);
    if (!user) {
      throw new Error(
        selfError
          ? `No Qtrust user found for email: ${email} (/self also failed: ${selfError})`
          : `No Qtrust user found for email: ${email}`
      );
    }
    return user;
  }

  // ============== CASE / APPROVAL OPERATIONS ==============

  private parseCases(data: any): MidpointCase[] {
    const items =
      data?.object?.case ||
      data?.object?.approvalCase ||
      data?.object?.object ||
      data?.case ||
      data?.approvalCase ||
      [];
    const list = Array.isArray(items) ? items : [items];
    return this.normalizeCases(list);
  }

  // Normalize all PolyString fields — Midpoint returns strings as {orig, norm, translation}
  private normalizeCases(list: any[]): MidpointCase[] {
    return (Array.isArray(list) ? list : []).map((c: any) => ({
      ...c,
      name: this.polyString(c.name),
      // Normalize workItem to always be an array, and normalize @id → id
      workItem: c.workItem
        ? (Array.isArray(c.workItem) ? c.workItem : [c.workItem]).map((wi: any) => ({
            ...wi,
            id: wi['@id'] ?? wi.id,
          }))
        : [],
      targetRef: c.targetRef ? {
        ...c.targetRef,
        targetName: this.polyString(c.targetRef.targetName),
      } : c.targetRef,
      requestorRef: c.requestorRef ? {
        ...c.requestorRef,
        targetName: this.polyString(c.requestorRef.targetName),
      } : c.requestorRef,
    }));
  }

  private async searchCases(filter: any): Promise<MidpointCase[]> {
    try {
      const url = `${this.baseUrl}/cases/search`;
      const headers = await getMidpointAuthHeaders();
      // null filter = no filter (fetch all cases) — must still send {"query":{}} not {}
      const queryBody = filter ? { query: { filter } } : { query: {} };
      const body = JSON.stringify(queryBody);
      console.debug('[searchCases] POST', url, queryBody);
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body,
      });
      const data = await response.json();
      console.debug('[searchCases] status:', response.status, 'data:', data);
      if (!response.ok) return [];
      return this.parseCases(data);
    } catch (err) {
      console.error('searchCases failed:', err);
      return [];
    }
  }

  async getMyAccessRequests(userOid: string): Promise<MidpointCase[]> {
    // TEMPORARY, for local BFF testing: the BFF resolves the caller from their
    // own bearer token, so userOid is unused here (kept for signature
    // compatibility). Server-side equivalent of the old client-side filtering
    // -- see bff/app/routes/cases.py.
    try {
      const data = await this.makeRequest<any[]>('/cases/my-requests');
      return this.normalizeCases(data);
    } catch (err) {
      console.error('getMyAccessRequests via BFF failed:', err);
      return [];
    }
  }

  async getPendingApprovals(userOid: string): Promise<MidpointCase[]> {
    // TEMPORARY, for local BFF testing: same as above -- the BFF replicates
    // the Superuser-sees-everything special case server-side now.
    try {
      const data = await this.makeRequest<any[]>('/cases/pending-approvals');
      return this.normalizeCases(data);
    } catch (err) {
      console.error('getPendingApprovals via BFF failed:', err);
      return [];
    }
  }

  async approveWorkItem(caseOid: string, workItemId: number): Promise<void> {
    console.debug('[approveWorkItem] caseOid:', caseOid, 'workItemId:', workItemId);
    await this.makeRequest(`/work-items/${caseOid}/${workItemId}/approve`, {
      method: 'POST',
      body: { comment: 'Approved' },
    });
  }

  async rejectWorkItem(caseOid: string, workItemId: number, comment: string = 'Rejected'): Promise<void> {
    await this.makeRequest(`/work-items/${caseOid}/${workItemId}/reject`, {
      method: 'POST',
      body: { comment },
    });
  }

  async cancelCase(caseOid: string): Promise<void> {
    await this.makeRequest(`/cases/${caseOid}/cancel`, { method: 'POST' });
  }
}

// ============== SINGLETON INSTANCE ==============

// Create default instance from environment variables
export const midpointApiService = new MidpointApiService();
